import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_SESSION_CHANGED } from 'src/auth/sessionEvents';
import {
  readWorkspaceSessionIdentity,
  retainWorkspaceSession
} from 'src/auth/workspaceSession';

/** Keep local drafts mounted but inaccessible to a replacement sign-in. */
export const WorkspaceSessionBoundary: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [owner] = useState(readWorkspaceSessionIdentity);
  const [changed, setChanged] = useState(false);
  const suspended = useRef(false);
  const queryClient = useQueryClient();
  useLayoutEffect(() => {
    const release = retainWorkspaceSession(owner);
    const check = () => {
      const mismatch = owner !== readWorkspaceSessionIdentity();
      if (mismatch && !suspended.current) {
        // Active observers and component drafts stay private in this hidden tab.
        // Cancel requests and evict inactive identity caches, not local form state.
        void queryClient.cancelQueries();
        queryClient.removeQueries({ type: 'inactive' });
      }
      if (!mismatch && suspended.current) {
        void queryClient.invalidateQueries();
      }
      suspended.current = mismatch;
      setChanged(mismatch);
    };
    window.addEventListener(AUTH_SESSION_CHANGED, check);
    window.addEventListener('focus', check);
    check();
    return () => {
      release();
      window.removeEventListener(AUTH_SESSION_CHANGED, check);
      window.removeEventListener('focus', check);
    };
  }, [owner, queryClient]);
  return (
    <>
      {changed && (
        <section
          role="alert"
          aria-labelledby="workspace-session-title"
          style={{
            margin: '48px auto',
            padding: 32,
            maxWidth: 640,
            background: '#fff',
            color: '#252525',
            borderRadius: 12,
            border: '1px solid #e7e1d7',
            lineHeight: 1.6,
            width: 'calc(100% - 32px)'
          }}
        >
          <h1
            id="workspace-session-title"
            style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}
          >
            Your sign-in changed
          </h1>
          <p>
            This page belongs to your previous account. Your unsaved edits are
            kept in this tab. Sign back into that account in another tab to
            continue here.
          </p>
          <p style={{ marginTop: 24, marginBottom: 12 }}>
            <a
              style={{ color: '#154c90', textDecoration: 'underline' }}
              href="/login"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign in to the original account
            </a>
          </p>
          <a
            style={{ color: '#154c90', textDecoration: 'underline' }}
            href="/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the current account in a new tab
          </a>
        </section>
      )}
      {changed && (
        <style>
          {
            'body > .MuiModal-root, body > .MuiPopper-root, body > .swal2-container { display: none !important; }'
          }
        </style>
      )}
      <div hidden={changed} aria-hidden={changed || undefined}>
        {children}
      </div>
    </>
  );
};
